/**
 * Thin API client for the admin SPA.
 *
 * - Always sends cookies (`credentials: 'include'`).
 * - Unwraps the standard `{ success, data, error }` envelope; if the response
 *   isn't enveloped, returns the parsed body as-is.
 * - Throws ApiError on non-2xx OR `success: false`.
 */

export interface ApiErrorPayload {
  code?: string;
  message?: string;
  details?: unknown;
}

export class ApiError extends Error {
  public code: string;
  public details?: unknown;
  public status: number;

  constructor(message: string, opts: { code?: string; details?: unknown; status: number }) {
    super(message);
    this.name = 'ApiError';
    this.code = opts.code ?? 'unknown_error';
    this.details = opts.details;
    this.status = opts.status;
  }
}

interface Envelope<T> {
  success?: boolean;
  data?: T;
  error?: ApiErrorPayload;
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

async function parseBody<T>(res: Response): Promise<T | undefined> {
  const ctype = res.headers.get('content-type') ?? '';
  if (res.status === 204) return undefined;
  if (ctype.includes('application/json')) {
    return (await res.json().catch(() => undefined)) as T | undefined;
  }
  // Non-JSON
  await res.text().catch(() => '');
  return undefined;
}

// ----------------------------------------------------------------------
// 401 handling: auto-refresh JWT + retry; on second failure redirect to login.
// One in-flight refresh shared across concurrent 401-ing calls.
// ----------------------------------------------------------------------
const REFRESH_PATH = '/api/iam/auth/refresh';
const LOGIN_ROUTE = '/login';
let pendingRefresh: Promise<boolean> | null = null;

async function attemptRefresh(): Promise<boolean> {
  if (pendingRefresh) return pendingRefresh;
  pendingRefresh = (async () => {
    try {
      const r = await fetch(REFRESH_PATH, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      });
      return r.ok;
    } catch {
      return false;
    } finally {
      // Reset so future 401s can trigger another refresh attempt.
      setTimeout(() => {
        pendingRefresh = null;
      }, 0);
    }
  })();
  return pendingRefresh;
}

function redirectToLogin(): void {
  if (typeof window === 'undefined') return;
  // Avoid a redirect loop if we're already on /login or its variants.
  const path = window.location.pathname;
  if (path.startsWith('/login') || path.startsWith('/auth/')) return;
  const ret = encodeURIComponent(window.location.pathname + window.location.search);
  window.location.assign(`${LOGIN_ROUTE}?return=${ret}`);
}

export async function api<T>(path: string, init?: RequestInit): Promise<T> {
  return apiWithRetry<T>(path, init, /* hasRetried */ false);
}

async function apiWithRetry<T>(
  path: string,
  init: RequestInit | undefined,
  hasRetried: boolean,
): Promise<T> {
  const headers = new Headers(init?.headers);
  if (init?.body && !(init.body instanceof FormData) && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  if (!headers.has('Accept')) {
    headers.set('Accept', 'application/json');
  }

  const res = await fetch(path, {
    credentials: 'include',
    ...init,
    headers,
  });

  const body = await parseBody<unknown>(res);

  if (res.status === 401 && !hasRetried && path !== REFRESH_PATH) {
    // Don't retry the login flow itself — that's a real auth failure to surface.
    const isLoginCall =
      path.startsWith('/api/iam/auth/login') || path.startsWith('/api/iam/auth/mfa');
    if (!isLoginCall) {
      const refreshed = await attemptRefresh();
      if (refreshed) {
        return apiWithRetry<T>(path, init, true);
      }
      redirectToLogin();
      // The browser is mid-navigation. We don't want React Query to render
      // a "Unauthorized Exception" banner before the page unmounts — return
      // a Promise that never resolves so the query stays in `pending` state
      // until the route changes and the component is unmounted.
      return new Promise<T>(() => {
        /* never resolves — redirect in progress */
      });
    }
  }

  if (!res.ok) {
    let code = `http_${res.status}`;
    let message = res.statusText || 'Request failed';
    let details: unknown;
    if (isPlainObject(body)) {
      const env = body as Envelope<unknown>;
      if (env.error && isPlainObject(env.error)) {
        const err = env.error as { code?: string; message?: string; details?: unknown };
        if (typeof err.code === 'string') code = err.code;
        if (typeof err.message === 'string') message = err.message;
        details = err.details;
      } else if (typeof (body as { message?: unknown }).message === 'string') {
        message = String((body as { message?: unknown }).message);
      }
    }
    throw new ApiError(message, { code, details, status: res.status });
  }

  // Unwrap envelope when present
  if (isPlainObject(body) && 'success' in body && 'data' in body) {
    const env = body as Envelope<T>;
    if (env.success === false) {
      throw new ApiError(env.error?.message ?? 'Request failed', {
        code: env.error?.code,
        details: env.error?.details,
        status: res.status,
      });
    }
    return env.data as T;
  }

  return body as T;
}

function withQuery(path: string, query?: Record<string, unknown>): string {
  if (!query) return path;
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null || v === '') continue;
    if (Array.isArray(v)) {
      for (const item of v) usp.append(k, String(item));
    } else {
      usp.set(k, String(v));
    }
  }
  const qs = usp.toString();
  if (!qs) return path;
  return `${path}${path.includes('?') ? '&' : '?'}${qs}`;
}

export const apiClient = {
  get<T>(path: string, query?: Record<string, unknown>): Promise<T> {
    return api<T>(withQuery(path, query), { method: 'GET' });
  },
  post<T>(path: string, body?: unknown): Promise<T> {
    return api<T>(path, {
      method: 'POST',
      body: body instanceof FormData ? body : body !== undefined ? JSON.stringify(body) : undefined,
    });
  },
  patch<T>(path: string, body?: unknown): Promise<T> {
    return api<T>(path, {
      method: 'PATCH',
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  },
  put<T>(path: string, body?: unknown): Promise<T> {
    return api<T>(path, {
      method: 'PUT',
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  },
  delete<T>(path: string): Promise<T> {
    return api<T>(path, { method: 'DELETE' });
  },
};
